import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';

import { getAccessToken } from './supabase_client';
import { createNetworkError } from './errors';

const retryableMethods = new Set(['get', 'head', 'options']);

function isRetryable(error: AxiosError<ErrorResponse>): boolean {
  const originalRequest = error.config;
  if (!originalRequest || originalRequest._retry) {
    return false;
  }

  const method = (originalRequest.method ?? '').toLowerCase();
  if (!retryableMethods.has(method)) {
    return false;
  }

  return error.response?.status === 401;
}

declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    _retry?: boolean;
  }
}

interface ErrorResponse {
  message: string;
  code: string;
}

function createClient(baseURL: string): AxiosInstance {
  return axios.create({
    baseURL,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    timeout: 15000, // 15 seconds
  });
}

function setupInterceptors(axiosInstance: AxiosInstance): void {
  axiosInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
      const token = await getAccessToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error: AxiosError): Promise<AxiosError> => {
      return Promise.reject(error);
    },
  );

  axiosInstance.interceptors.response.use(
    (response: AxiosResponse): AxiosResponse => {
      return response;
    },
    async (error: AxiosError<ErrorResponse>): Promise<any> => {
      if (!isRetryable(error)) {
        return Promise.reject(createNetworkError(error));
      }

      const originalRequest = error.config!;

      originalRequest._retry = true;

      try {
        const token = await getAccessToken();

        if (token && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
        }

        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // TODO: Handle refresh token failure
        // toast or redirect to login?
        return Promise.reject(refreshError);
      }
    },
  );
}

export const client: AxiosInstance = createClient(import.meta.env.VITE_API_BASE_URL);
setupInterceptors(client);

export const client_query_engine: AxiosInstance = createClient(
  import.meta.env.VITE_QUERY_ENGINE_BASE_URL,
);
setupInterceptors(client_query_engine);

export default client;
